import { useTranslation } from 'react-i18next';

import { Input, Pagination, Spin } from '@arco-design/web-react';

import { EmptyTips } from '@/components';
import { RepoSelectorCard } from '@/components-biz/RepoSelectorCard';
import type { MaasModelSourceModelPlaza } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import type { ArtifactItem } from '@/pages/data-center/artifact-center/shared';

import { useModelPlazaRepoList } from './hooks/useModelPlazaRepoList';
import styles from './index.module.less';
import { getModelPlazaArtifactSize, getModelPlazaSourceFromArtifact, isModelPlazaSourceMatch } from './utils';

export { getModelPlazaArtifactSize, getModelPlazaSourceFromArtifact, isModelPlazaSourceMatch } from './utils';
export { useModelPlazaRepoList } from './hooks/useModelPlazaRepoList';

export interface ModelPlazaRepoSelectorProps {
  disabled?: boolean;
  /** 与 model.source.modelplaza 对齐 */
  value?: Partial<MaasModelSourceModelPlaza>;
  onChange?: (source: MaasModelSourceModelPlaza, item: ArtifactItem) => void;
}

/** 模型广场模型仓库选择（对应后端 model.source.modelplaza） */
export function ModelPlazaRepoSelector({ disabled = false, value, onChange }: ModelPlazaRepoSelectorProps) {
  const { t } = useTranslation();
  const { items, loading, page, limit, total, inputValue, onInputChange, onSearch, onPageChange } = useModelPlazaRepoList();

  const handleSelect = (item: ArtifactItem) => {
    if (disabled) return;
    const source = getModelPlazaSourceFromArtifact(item);
    if (!source.model || !source.url) return;
    onChange?.(source, item);
  };

  return (
    <div className={styles.container}>
      <div className={styles.searchBar}>
        <h5 className={styles.title}>{t('home:market.model.repository')}</h5>
        <Input.Search
          placeholder={t('data-center:maas.model.plaza.search')}
          allowClear
          value={inputValue}
          onChange={onInputChange}
          onSearch={onSearch}
          onClear={() => onSearch('')}
          disabled={disabled}
          style={{ width: '40%' }}
        />
      </div>

      <Spin loading={loading} block>
        <div className={styles.repoGrid}>
          {items.map((item) => {
            const source = getModelPlazaSourceFromArtifact(item);
            const size = getModelPlazaArtifactSize(item);
            const selected = isModelPlazaSourceMatch(item, value);
            return (
              <RepoSelectorCard
                key={item.id}
                name={item.name}
                metaItems={[source.model, item.vendor, size]}
                address={source.url}
                selected={selected}
                disabled={disabled}
                onClick={() => handleSelect(item)}
              />
            );
          })}
        </div>

        {!loading && items.length === 0 ? (
          <div className={styles.emptyWrapper}>
            <EmptyTips tips={t('data-center:maas.model.plaza.empty')} />
          </div>
        ) : null}

        {total > limit ? (
          <div className={styles.paginationWrapper}>
            <Pagination size="mini" current={page} pageSize={limit} total={total} onChange={onPageChange} showTotal disabled={disabled} />
          </div>
        ) : null}
      </Spin>
    </div>
  );
}
